import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Calculator, TrendingUp, Info, DollarSign } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const STORAGE_KEY = 'car_loan_data';

const CarLoanCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).loanAmount : 500000;
  });
  
  const [interestRate, setInterestRate] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).interestRate : 8.5;
  });
  
  const [loanTenure, setLoanTenure] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).loanTenure : 60;
  });
  
  const [downPayment, setDownPayment] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).downPayment : 100000;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      loanAmount,
      interestRate,
      loanTenure,
      downPayment
    }));
  }, [loanAmount, interestRate, loanTenure, downPayment]);

  const calculateLoan = () => {
    const principal = parseFloat(loanAmount) || 0;
    const rate = (parseFloat(interestRate) || 0) / 100 / 12;
    const months = parseFloat(loanTenure) || 0;
    const down = parseFloat(downPayment) || 0;
    
    const loanAmountAfterDown = principal - down;
    const emi = loanAmountAfterDown > 0 && rate > 0 && months > 0
      ? (loanAmountAfterDown * rate * Math.pow(1 + rate, months)) / (Math.pow(1 + rate, months) - 1)
      : 0;
    
    const totalPayable = emi * months;
    const totalInterest = totalPayable - loanAmountAfterDown;
    const totalCost = totalPayable + down;

    return {
      emi: Math.round(emi),
      totalPayable: Math.round(totalPayable),
      totalInterest: Math.round(totalInterest),
      totalCost: Math.round(totalCost),
      loanAmountAfterDown: Math.round(loanAmountAfterDown),
      principalPercentage: totalPayable > 0 ? (loanAmountAfterDown / totalPayable) * 100 : 0,
      interestPercentage: totalPayable > 0 ? (totalInterest / totalPayable) * 100 : 0
    };
  };

  const generateAmortizationSchedule = () => {
    const principal = parseFloat(loanAmount) - parseFloat(downPayment);
    const rate = (parseFloat(interestRate) || 0) / 100 / 12;
    const months = parseFloat(loanTenure) || 0;
    const results = calculateLoan();
    const emi = results.emi;

    let balance = principal;
    const schedule = [];
    
    for (let month = 1; month <= months && balance > 0; month++) {
      const interestPayment = balance * rate;
      const principalPayment = emi - interestPayment;
      balance -= principalPayment;
      
      if (month === 1 || month % 12 === 0 || month === months) {
        schedule.push({
          month: `Month ${month}`,
          monthNum: month,
          principal: Math.round(principalPayment),
          interest: Math.round(interestPayment),
          balance: Math.round(Math.max(0, balance))
        });
      }
    }
    
    return schedule;
  };

  const generateInterestOverTime = () => {
    const principal = parseFloat(loanAmount) - parseFloat(downPayment);
    const rate = (parseFloat(interestRate) || 0) / 100 / 12;
    const months = parseFloat(loanTenure) || 0;
    const results = calculateLoan();
    const emi = results.emi;

    let balance = principal;
    let cumulativeInterest = 0;
    let cumulativePrincipal = 0;
    const data = [];
    
    for (let month = 1; month <= months && balance > 0; month++) {
      const interestPayment = balance * rate;
      const principalPayment = emi - interestPayment;
      balance -= principalPayment;
      cumulativeInterest += interestPayment;
      cumulativePrincipal += principalPayment;
      
      if (month === 1 || month % 6 === 0 || month === months) {
        data.push({
          month: `M${month}`,
          monthNum: month,
          interest: Math.round(cumulativeInterest),
          principal: Math.round(cumulativePrincipal)
        });
      }
    }
    
    return data;
  };

  const results = calculateLoan();
  const amortizationSchedule = generateAmortizationSchedule();
  const interestOverTime = generateInterestOverTime();

  const pieData = [
    { name: 'Principal', value: results.loanAmountAfterDown },
    { name: 'Interest', value: results.totalInterest }
  ];

  const COLORS = ['hsl(var(--chart-2))', 'hsl(var(--chart-1))'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Car Loan Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is a Car Loan EMI Calculator?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  A Car Loan EMI Calculator helps you determine your monthly installment (EMI) based on the loan amount, 
                  interest rate, and tenure. It provides a complete breakdown of your total payment, including principal 
                  and interest components.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Input Parameters</CardTitle>
                  <CardDescription>Enter your loan details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="loanAmount" className="text-sm font-medium">
                      Loan Amount ($)
                    </Label>
                    <Input
                      id="loanAmount"
                      type="number"
                      value={loanAmount}
                      onChange={(e) => setLoanAmount(e.target.value)}
                      placeholder="500000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Total vehicle price</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="downPayment" className="text-sm font-medium">
                      Down Payment ($)
                    </Label>
                    <Input
                      id="downPayment"
                      type="number"
                      value={downPayment}
                      onChange={(e) => setDownPayment(e.target.value)}
                      placeholder="100000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Initial payment</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="interestRate" className="text-sm font-medium">
                      Interest Rate (% per annum)
                    </Label>
                    <Input
                      id="interestRate"
                      type="number"
                      value={interestRate}
                      onChange={(e) => setInterestRate(e.target.value)}
                      placeholder="8.5"
                      step="0.1"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="loanTenure" className="text-sm font-medium">
                      Loan Tenure (Months)
                    </Label>
                    <Input
                      id="loanTenure"
                      type="number"
                      value={loanTenure}
                      onChange={(e) => setLoanTenure(e.target.value)}
                      placeholder="60"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">{(loanTenure / 12).toFixed(1)} years</p>
                  </div>

                  <Button 
                    className="w-full gradient-primary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setLoanAmount(loanAmount)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Car Loan Analysis Results</CardTitle>
                    <CardDescription>Your EMI and payment breakdown</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Monthly EMI</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {formatCurrency(results.emi)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">per month</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Interest Payable</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {formatCurrency(results.totalInterest)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Loan Cost</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent">
                          {formatCurrency(results.totalPayable)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">excluding down payment</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-purple-light to-card border border-purple/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Amount Payable</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-purple-600">
                          {formatCurrency(results.totalCost)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">including down payment</p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="p-6 rounded-xl bg-gradient-chart">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Loan Amount (After Down Payment)</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {formatCurrency(results.loanAmountAfterDown)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Loan Tenure</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {loanTenure} months
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {(loanTenure / 12).toFixed(1)} years
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">EMI Breakup (Principal vs Interest)</CardTitle>
                <CardDescription className="text-base">
                  Understanding how your payment is split between principal and interest
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Outstanding Loan Balance Over Time</CardTitle>
                  <CardDescription>
                    How your loan balance decreases
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={amortizationSchedule}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="month" 
                          stroke="hsl(var(--muted-foreground))"
                          angle={-15}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="balance" 
                          stroke="hsl(var(--primary))" 
                          strokeWidth={3}
                          name="Outstanding Balance"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Interest Paid Over Tenure</CardTitle>
                  <CardDescription>
                    Cumulative principal and interest payments
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={interestOverTime}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="month" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Bar dataKey="principal" stackId="a" fill="hsl(var(--chart-2))" name="Cumulative Principal" />
                        <Bar dataKey="interest" stackId="a" fill="hsl(var(--chart-1))" name="Cumulative Interest" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Your Car Loan EMI</CardTitle>
                <CardDescription className="text-base">
                  Learn how EMIs work and make informed decisions about your car loan
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    How is EMI Calculated?
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Your monthly EMI is <strong className="text-primary">{formatCurrency(results.emi)}</strong>. 
                    This is calculated using the reducing balance method, where interest is charged on the outstanding balance.
                  </p>
                  <div className="p-4 rounded-lg bg-muted/50 text-sm">
                    <p className="font-mono mb-2">EMI = [P × R × (1+R)^N] / [(1+R)^N-1]</p>
                    <p className="mb-1">Where:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>P = Principal loan amount: {formatCurrency(results.loanAmountAfterDown)}</li>
                      <li>R = Monthly interest rate: {(interestRate / 12).toFixed(3)}%</li>
                      <li>N = Loan tenure in months: {loanTenure}</li>
                    </ul>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Effect of Tenure on Interest
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Your total interest payable is <strong className="text-secondary">{formatCurrency(results.totalInterest)}</strong>. 
                    This represents <strong>{results.interestPercentage.toFixed(1)}%</strong> of your total payment.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-secondary-light border border-secondary/20">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Shorter Tenure Impact</p>
                      <p className="text-sm text-muted-foreground">
                        Higher EMI, but significantly lower total interest paid. You own the car faster.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border border-secondary/20">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Longer Tenure Impact</p>
                      <p className="text-sm text-muted-foreground">
                        Lower EMI makes it affordable, but you pay much more in interest over time.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Important Risks & Considerations
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Long Tenure Cost Inflation:</strong> A longer tenure means 
                        you'll pay significantly more in interest. For example, doubling your tenure can increase total 
                        interest by 50-80%.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Depreciation Risk:</strong> Cars depreciate rapidly. 
                        Ensure your loan tenure doesn't exceed the period you plan to use the car.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Budget Strain:</strong> Keep EMI below 15-20% of your 
                        monthly income to maintain financial flexibility.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Processing Fees & Charges:</strong> Remember to factor in 
                        processing fees, insurance, and other charges not included in EMI.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple-600">
                    Smart Loan Strategies
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Higher Down Payment vs Shorter Tenure</h4>
                      <p className="text-muted-foreground">
                        A higher down payment reduces your principal, lowering both EMI and total interest. 
                        Aim for 20-30% down payment to get better interest rates and reduce financial burden.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Make Prepayments When Possible</h4>
                      <p className="text-muted-foreground">
                        Even small prepayments can significantly reduce your total interest. Check if your lender 
                        allows prepayment without penalties.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">✅ Compare Interest Rates</h4>
                      <p className="text-muted-foreground">
                        Even a 0.5% difference in interest rate can save thousands over the loan tenure. 
                        Shop around for the best rates from different lenders.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-light border-l-4 border-purple-500">
                      <h4 className="font-semibold text-foreground mb-2">✅ Choose Appropriate Tenure</h4>
                      <p className="text-muted-foreground">
                        Balance between affordable EMI and lower interest. Typically, 3-5 years is optimal for 
                        most car loans to balance cost and affordability.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CarLoanCalculator;